import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Block as RevokeIcon,
  ContentCopy as CopyIcon,
  LocalShipping as VehicleIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  RvHookup as TrailerIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import {
  ShareAssetType,
  ShareLinkListItem,
  useGetShareLinksQuery,
  useRevokeShareLinkMutation,
} from "../../api/shareApi";
import { DataTable, Column } from "../../components/common/DataTable/DataTable";
import { ConfirmationDialog } from "../../components/common/ConfirmationDialog/ConfirmationDialog";
import PageTitle from "../../components/common/PageTitle";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import PageLayout from "../../components/layout/PageLayout";
import { ShareLinkDialog } from "../dashboard/components/map/ShareLinkDialog";

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const getStatus = (link: ShareLinkListItem) => {
  if (!link.isActive) return { label: "Revoked", color: "default" as const };
  if (link.isExpired) return { label: "Expired", color: "warning" as const };
  return { label: "Active", color: "success" as const };
};

const getDisplayName = (link: ShareLinkListItem) =>
  link.name || `${link.items.length} asset${link.items.length === 1 ? "" : "s"}`;

const getAssetSummary = (items: ShareLinkListItem["items"]) => {
  const vehicleCount = items.filter((item) => item.assetType === ShareAssetType.Vehicle).length;
  const trailerCount = items.filter((item) => item.assetType === ShareAssetType.Trailer).length;
  const parts: string[] = [];

  if (vehicleCount > 0) {
    parts.push(`${vehicleCount} vehicle${vehicleCount === 1 ? "" : "s"}`);
  }

  if (trailerCount > 0) {
    parts.push(`${trailerCount} trailer${trailerCount === 1 ? "" : "s"}`);
  }

  return parts.join(", ") || "No assets";
};

const ShareLinksPage: React.FC = () => {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [linkToRevoke, setLinkToRevoke] = useState<ShareLinkListItem | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: links = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetShareLinksQuery();
  const [revokeShareLink] = useRevokeShareLinkMutation();

  const activeLinks = useMemo(
    () => links.filter((link) => link.isActive && !link.isExpired),
    [links]
  );
  const inactiveLinks = useMemo(
    () => links.filter((link) => !link.isActive || link.isExpired),
    [links]
  );

  const handleCopy = async (link: ShareLinkListItem) => {
    await copyText(link.shareUrl);
    setCopiedToken(link.token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleRevoke = (link: ShareLinkListItem) => {
    setLinkToRevoke(link);
  };

  const handleCloseRevokeDialog = () => {
    if (revokingToken) return;
    setLinkToRevoke(null);
  };

  const handleConfirmRevoke = async () => {
    if (!linkToRevoke) return;

    try {
      setActionError(null);
      setRevokingToken(linkToRevoke.token);
      await revokeShareLink(linkToRevoke.token).unwrap();
      setLinkToRevoke(null);
    } catch (err) {
      console.error("Failed to revoke share link:", err);
      setActionError("Failed to revoke share link. Please try again.");
    } finally {
      setRevokingToken(null);
    }
  };

  const columns: Column[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      renderCell: (_value, row: ShareLinkListItem) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {getDisplayName(row)}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace" noWrap>
            {row.token}
          </Typography>
        </Box>
      ),
    },
    {
      field: "items",
      headerName: "Assets",
      flex: 1.6,
      renderCell: (items: ShareLinkListItem["items"]) => {
        const vehicles = items.filter((item) => item.assetType === ShareAssetType.Vehicle);
        const trailers = items.filter((item) => item.assetType === ShareAssetType.Trailer);
        const summary = getAssetSummary(items);

        if (items.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          );
        }

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ maxWidth: 320 }}>
                {vehicles.length > 0 && (
                  <Box sx={{ mb: trailers.length > 0 ? 1 : 0 }}>
                    <Typography variant="caption" fontWeight={700}>
                      Vehicles
                    </Typography>
                    {vehicles.map((item, index) => (
                      <Typography key={`vehicle-${item.label}-${index}`} variant="body2">
                        {item.label}
                      </Typography>
                    ))}
                  </Box>
                )}
                {trailers.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>
                      Trailers
                    </Typography>
                    {trailers.map((item, index) => (
                      <Typography key={`trailer-${item.label}-${index}`} variant="body2">
                        {item.label}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            }
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                cursor: "default",
                textDecoration: "underline dotted",
                textUnderlineOffset: 3,
              }}
            >
              {vehicles.length > 0 && (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
                  <VehicleIcon sx={{ fontSize: 17, color: "primary.main" }} />
                  <Typography variant="body2">
                    {vehicles.length} vehicle{vehicles.length === 1 ? "" : "s"}
                  </Typography>
                </Box>
              )}
              {trailers.length > 0 && (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
                  <TrailerIcon sx={{ fontSize: 17, color: "secondary.main" }} />
                  <Typography variant="body2">
                    {trailers.length} trailer{trailers.length === 1 ? "" : "s"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "createdByUserName",
      headerName: "Created By",
      flex: 0.8,
      renderCell: (value: string | null) => (
        <Typography variant="body2" color={value ? "text.primary" : "text.secondary"}>
          {value || "Unknown"}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 0.9,
      renderCell: (value: string) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      field: "expiresAt",
      headerName: "Expires",
      flex: 0.9,
      renderCell: (value: string) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: (_value, row: ShareLinkListItem) => {
        const status = getStatus(row);
        return <Chip label={status.label} color={status.color} size="small" />;
      },
    },
  ];

  const getActions = (link: ShareLinkListItem) => [
    {
      icon: copiedToken === link.token ? <ShareIcon /> : <CopyIcon />,
      label: copiedToken === link.token ? "Copied" : "Copy link",
      onClick: () => handleCopy(link),
      color: copiedToken === link.token ? ("success" as const) : ("primary" as const),
    },
    {
      icon: <OpenInNewIcon />,
      label: "Open link",
      onClick: () => window.open(link.shareUrl, "_blank", "noopener,noreferrer"),
      color: "info" as const,
    },
    {
      icon: <RevokeIcon />,
      label: revokingToken === link.token ? "Revoking..." : "Revoke",
      onClick: () => handleRevoke(link),
      color: "error" as const,
      disabled: !link.isActive || link.isExpired || revokingToken === link.token,
    },
  ];

  const renderTable = (items: ShareLinkListItem[]) => (
    <DataTable
      items={items}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      isLoadingRows={isFetching && !isLoading}
      totalCount={items.length}
      pageSize={10}
      rowsPerPageOptions={[10, 25, 50]}
    />
  );

  return (
    <PageLayout variant="fullHeight">
      <PageTitle
        title="Share Links"
        subtitle="Review public tracking links, copy URLs, and revoke links that should no longer work."
        icon={<ShareIcon />}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={() => setIsCreateDialogOpen(true)}
            >
              Generate Link
            </Button>
            <Tooltip title="Refresh share links">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          </Box>
        }
      />

      {(error || actionError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError || "Failed to load share links."}
        </Alert>
      )}

      <TabbedPageCard
        idPrefix="share-links"
        ariaLabel="share link tabs"
        fullHeight
        panelSx={{ p: 2, overflow: "hidden" }}
        tabs={[
          {
            label: `Active (${activeLinks.length})`,
            content: renderTable(activeLinks),
          },
          {
            label: `Expired / Revoked (${inactiveLinks.length})`,
            content: renderTable(inactiveLinks),
          },
        ]}
      />

      <ShareLinkDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />

      <ConfirmationDialog
        open={!!linkToRevoke}
        title="Revoke Share Link"
        message={`Revoke "${linkToRevoke ? getDisplayName(linkToRevoke) : "this link"}"? This public tracking link will stop working immediately.`}
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        confirmColor="error"
        onClose={handleCloseRevokeDialog}
        onConfirm={handleConfirmRevoke}
        isLoading={Boolean(revokingToken)}
      />
    </PageLayout>
  );
};

export default ShareLinksPage;
